/**
 * Environment Detection Utilities
 * 
 * This utility provides consistent environment detection across the application
 * to control debug features, security bypasses, and development tools.
 */

export type Environment = 'development' | 'staging' | 'production';

/**
 * Detect the current environment based on hostname and build mode
 */
export const detectEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  
  // Staging environment
  if (hostname.includes('staging') || hostname === 'staging.sequenceplanner.com') {
    return 'staging';
  }
  
  // Production environment (app.sequenceplanner.com or custom domain)
  return 'production';
};

/**
 * Check if we're in a development environment (localhost)
 */
export const isDevelopment = (): boolean => {
  return detectEnvironment() === 'development';
};

/**
 * Check if we're in staging environment
 */
export const isStaging = (): boolean => {
  return detectEnvironment() === 'staging';
};

/**
 * Check if we're in production environment
 */
export const isProduction = (): boolean => {
  return detectEnvironment() === 'production';
};

/**
 * Check if debug features should be shown
 * Debug features are available in development and staging, but NOT in production
 */
export const shouldShowDebugFeatures = (): boolean => {
  const env = detectEnvironment();
  return env === 'development' || env === 'staging';
};

/**
 * Check if security bypasses should be allowed
 * Security bypasses are ONLY allowed in development, NOT in staging or production
 */
export const shouldAllowSecurityBypasses = (): boolean => {
  return detectEnvironment() === 'development';
};

/**
 * Check if console logging should be enabled
 * Console logs are allowed in development and staging for debugging
 */
export const shouldEnableConsoleLogging = (): boolean => {
  const env = detectEnvironment();
  return env === 'development' || env === 'staging';
};

/**
 * Get environment-specific configuration
 */
export const getEnvironmentConfig = () => {
  const env = detectEnvironment();
  
  return {
    environment: env,
    showDebugFeatures: shouldShowDebugFeatures(),
    allowSecurityBypasses: shouldAllowSecurityBypasses(),
    enableConsoleLogging: shouldEnableConsoleLogging(),
    isProduction: isProduction(),
    isStaging: isStaging(),
    isDevelopment: isDevelopment()
  };
};

/**
 * Safe console logging that respects environment
 */
export const safeConsoleLog = (message: string, ...args: any[]) => {
  if (shouldEnableConsoleLogging()) {
    console.log(`[${detectEnvironment().toUpperCase()}] ${message}`, ...args);
  }
};

/**
 * Safe console error logging (always enabled for debugging production issues)
 */
export const safeConsoleError = (message: string, ...args: any[]) => {
  console.error(`[${detectEnvironment().toUpperCase()}] ${message}`, ...args);
};

/**
 * Safe console warning (always enabled for important warnings)
 */
export const safeConsoleWarn = (message: string, ...args: any[]) => {
  console.warn(`[${detectEnvironment().toUpperCase()}] ${message}`, ...args);
};
